import { useEffect, useRef, useState } from "react";
import {
  auth,
  onAuthChange,
  GatewayClient,
  useStore,
  VoiceClient,
} from "@tasca/client-sdk";
import { startDesktopIntegration } from "./lib/desktopBootstrap.js";
import { loadSettings, saveSettings } from "./lib/voiceStorage.js";
import { Auth } from "./screens/Auth.js";
import { Chat } from "./screens/Chat.js";
import { Overlays } from "./ui/Overlays.js";

export function App(): React.ReactElement {
  const me = useStore((s) => s.me);
  const applyEvent = useStore((s) => s.applyEvent);
  const setStatus = useStore((s) => s.setStatus);
  const reset = useStore((s) => s.reset);

  const [booting, setBooting] = useState(true);
  const gateway = useRef<GatewayClient | null>(null);
  const voice = useRef<VoiceClient | null>(null);

  if (!voice.current) {
    const store = useStore.getState;
    voice.current = new VoiceClient(
      {
        onState: (s, detail) => store().setVoiceState(s, detail),
        onStream: (stream) => store().addRemoteStream(stream),
        onStreamEnded: (consumerId) => store().removeRemoteStream(consumerId),
        // Os nomes vem do SFU; a lista autoritativa de participantes vem do
        // gateway, logo aqui so precisamos dos streams.
        onPeers: () => {},
        onSpeaking: (peerId, speaking) => store().setSpeaking(peerId, speaking),
        // A fala dos outros chega por SPEAKING_UPDATE; a propria sai por aqui,
        // ja coalescida pelo cliente de voz.
        onLocalSpeaking: (speaking) => gateway.current?.speaking(speaking),
        onQuality: (quality, stats) => store().setVoiceQuality(quality, stats),
        onLocalState: (state) => {
          store().setVoiceLocalState(state);
          // Os outros só vêem o icone de microfone desligado porque o ESTADO
          // de voz o diz — o `producerPaused` do SFU nao sai do canal de
          // sinalizacao. Reenviar o op 9 e o que mantem a lista de
          // participantes honesta para todos.
          const channelId = voice.current?.channelId;
          if (!channelId) return;
          gateway.current?.send(9, {
            channelId,
            selfMute: state.muted,
            selfDeaf: state.deafened,
            selfVideo: voice.current?.cameraOn ?? false,
            selfStream: voice.current?.screenOn ?? false,
          });
        },
        onSettings: (settings) => store().setVoiceSettings(settings),
        onDevices: (devices) => store().setVoiceDevices(devices),
        onVideoStatus: (consumerId, status) =>
          store().setVideoStatus(consumerId, status),
      },
      { settings: loadSettings(), persist: saveSettings },
    );
  }

  // Um só cliente de gateway para a vida da aplicação.
  if (!gateway.current) {
    gateway.current = new GatewayClient({
      onEvent: (event, data) => applyEvent(event, data),
      onStatus: (s) => setStatus(s),
      // Token morto: voltar ao login em vez de reconectar em ciclo.
      onFatal: () => reset(),
    });
  }

  /**
   * Arranque: tenta recuperar a sessão a partir do cookie HttpOnly. É o que
   * faz um F5 não exigir novo login — o access token vive só em memória e
   * desaparece com o reload, de propósito.
   *
   * Não vamos buscar o perfil por REST: se o refresh funcionar, o
   * `onAuthChange` liga o gateway e o READY traz o utilizador, os guilds e
   * os estados de leitura num só evento. Um GET /users/@me aqui seria um
   * round-trip para informação que já vem a caminho.
   */
  useEffect(() => {
    let cancelled = false;
    void auth.restore().finally(() => {
      if (!cancelled) setBooting(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Integracao nativa: push-to-talk global, badge do tray, notificacoes do
  // SO. Num browser nao faz nada — ver lib/desktop.ts.
  useEffect(() => startDesktopIntegration(voice.current!), []);

  // O gateway liga quando aparece um token e desliga quando ele desaparece.
  // `setAccessToken` é a única fonte deste sinal, logo login, refresh e
  // logout passam todos por aqui sem lógica duplicada.
  useEffect(() => {
    const client = gateway.current!;
    return onAuthChange((authed) => {
      if (authed) client.connect();
      else client.disconnect();
    });
  }, []);

  const screen = booting ? (
    <div className="boot">
      <div className="mark">T</div>
      <p>A ligar…</p>
    </div>
  ) : !me ? (
    <Auth />
  ) : (
    <Chat gateway={gateway.current} voice={voice.current} />
  );

  // Os avisos e as perguntas vivem acima do ecrã activo: não se perdem num
  // logout nem dependem de qual é o ecrã montado.
  return (
    <>
      {screen}
      <Overlays />
    </>
  );
}
